'use client'
import { dispatch, useSelector } from '@/lib/store'
import LocationPromptModal from './Modal/LocationPromptModal'
import { Dispatch, Fragment, SetStateAction, Suspense } from 'react'
import ErrorModal from '../LandingPage/ErrorModal'
import { updatePackageData } from '@/lib/reducers/packageReducer'
import { updateCommonReducerData } from '@/lib/reducers/commonReducer'
import CommonSearchInput from './CommonSearchInput'
import { ChevronDown, MapPin } from 'lucide-react'
import { loadState } from '@/utils/localStorage'

interface MWebLocationAndSearchBarProps {
  className?: string
  locationModalIsOpen?: boolean
  setLocationModalIsOpen: Dispatch<SetStateAction<boolean>>
  isMounted: boolean
  isCityListFetched: boolean
  showSearchBar?: boolean
}

const MWebLocationAndSearchBar = ({
  className = 'searchbar',
  locationModalIsOpen = false,
  setLocationModalIsOpen,
  isMounted,
  isCityListFetched,
  showSearchBar = true,
}: MWebLocationAndSearchBarProps) => {
  const packageState = useSelector((state) => state?.package) || {}
  const commonState = useSelector((state) => state.common) || {}
  const cityState = useSelector((state) => state.city) || {}

  const currentSelectedCity = cityState?.currentCityObj
  const selectedPincode = loadState('selectedPincode') || ''

  const handleOpenSearchModal = () => {
    dispatch(updatePackageData({ ...packageState, modalIsOpen: true }))
  }

  return (
    <Fragment>
      {/* Mobile city selector */}
      <div className="lg:hidden flex items-center pb-2 w-full">
        {!isMounted || !isCityListFetched ? (
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 bg-gray-200 animate-pulse rounded-full flex-shrink-0" />
            <div className="h-4 w-[60px] bg-gray-200 animate-pulse rounded" />
          </div>
        ) : (
          <button
            className="flex items-center gap-1.5"
            onClick={() => setLocationModalIsOpen(true)}
          >
            <MapPin size={16} className="flex-shrink-0 text-primary-500" />
            <div className="flex gap-0.5 items-center">
              <span className="text-body-sm font-medium text-gray-800 capitalize">
                {currentSelectedCity?.name?.toLowerCase() || ''}
                {selectedPincode ?? ''}
              </span>
              <ChevronDown size={14} className="flex-shrink-0 text-gray-800" />
            </div>
          </button>
        )}
      </div>

      {/* Mobile search bar - renders as position:fixed via CSS */}
      {showSearchBar && (
        <div className={`${className} divide-x-0 sm:divide-x divide-gray-300`}>
          <CommonSearchInput
            readOnly
            placeholder="Search for Tests/Packages"
            onClick={handleOpenSearchModal}
            containerClassName="w-full min-w-[300px] sm:min-w-[400px]"
          />
        </div>
      )}

      <Suspense fallback={<div></div>}>
        <LocationPromptModal
          open={locationModalIsOpen}
          onOpenChange={setLocationModalIsOpen}
        />
      </Suspense>
      {commonState?.pincodeNotServicableModalIsOpen && (
        <ErrorModal
          open={commonState?.pincodeNotServicableModalIsOpen}
          handleSelectLocationManually={() => {
            dispatch(
              updateCommonReducerData({
                ...commonState,
                pincodeNotServicableModalIsOpen: false,
              })
            )
            setLocationModalIsOpen(true)
          }}
        />
      )}
    </Fragment>
  )
}

export default MWebLocationAndSearchBar
